import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import useGuestDonations from "hooks/donations/useGuestDonations";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const STATUS_STYLES = {
  ACTIVE: { bg: "#E8F7E3", text: "#2E7D32", label: "Active" },
  PENDING: { bg: "#FFF4E5", text: "#ED6C02", label: "Pending" },
  FAILED: { bg: "#FDECEA", text: "#C62828", label: "Failed" },
};

const getPurposeLabel = (purpose) => {
  if (!purpose) return null;
  if (typeof purpose === "string") return purpose;
  return purpose.name || purpose.code || purpose.id || null;
};

const GuestDonationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { fetchDonationById } = useGuestDonations();

  useEffect(() => {
    const fetchDonation = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchDonationById(id);
        setDonation(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDonation();
  }, [id]);

  const formatDate = (epochMs) => {
    if (!epochMs) return "Not provided";
    return new Date(epochMs).toLocaleString();
  };

  const formatAmount = (amount) => {
    if (amount == null) return "$0.00";
    return `$${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  if (error || !donation) {
    return (
      <div className="container mt-4">
        <WidgetSectionTitle
          icon="hands-helping"
          title="Guest Donation Details"
          subtitle="View donation details"
          transform="shrink-2"
          className="mb-3"
        />
        <Card>
          <Card.Body>
            <p className="text-danger">Error loading donation: {error}</p>
            <Button variant="secondary" onClick={() => navigate("/olitask/donations/guest")}>
              Back to Guest Donations
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const status = STATUS_STYLES[donation.status] || {
    bg: "#F3F4F6",
    text: "#374151",
    label: donation.status || "Unknown",
  };
  const purposeLabels = (donation.purposes || []).map(getPurposeLabel).filter(Boolean);

  return (
    <div className="container mt-4">
      <WidgetSectionTitle
        icon="hands-helping"
        title="Guest Donation Details"
        subtitle="View donation details"
        transform="shrink-2"
        className="mb-3"
      />

      <div className="mb-3">
        <Button variant="link" onClick={() => navigate("/olitask/donations/guest")}>
          &larr; Back to Guest Donations
        </Button>
      </div>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Donation Information</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Donation ID</Col>
                <Col sm={8}>{donation.id}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Code</Col>
                <Col sm={8}>{donation.code || "N/A"}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Amount</Col>
                <Col sm={8}>{formatAmount(donation.amount)}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Balance</Col>
                <Col sm={8}>{formatAmount(donation.balance)}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Currency</Col>
                <Col sm={8}>{donation.currency || "USD"}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Status</Col>
                <Col sm={8}>
                  <span
                    style={{
                      backgroundColor: status.bg,
                      color: status.text,
                      padding: "3px 8px",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {status.label}
                  </span>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Anonymous</Col>
                <Col sm={8}>{donation.anonymous ? "Yes" : "No"}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Note</Col>
                <Col sm={8}>{donation.note || "N/A"}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Purposes</Col>
                <Col sm={8}>
                  {purposeLabels.length ? (
                    <div className="d-flex flex-wrap gap-1">
                      {purposeLabels.map((label) => (
                        <Badge key={label} bg="success" className="text-wrap">
                          {label}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    "N/A"
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Timestamps & Payment</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Created At</Col>
                <Col sm={8}>{formatDate(donation.createdAt)}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Updated At</Col>
                <Col sm={8}>{formatDate(donation.updatedAt)}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4} className="text-muted">Payment ID</Col>
                <Col sm={8}>{donation.guestPaymentChargeId || "N/A"}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default GuestDonationDetails;